import { useEffect } from 'react';

import { getSystem } from '@/system';

// How long to give the landing screen before preloading the player. Tune these
// against real device timings if the handoff to home still janks.
/** Longest to wait for browser idle time before preloading anyway. */
export const PLAYER_PRELOAD_IDLE_TIMEOUT_MS = 4200;
/** Fixed delay used where requestIdleCallback isn't supported. */
export const PLAYER_PRELOAD_FALLBACK_DELAY_MS = 2500;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

// Routes that hand off to somewhere else rather than being where the user lands:
// splash, profile picker / PIN screens, and the deep-link router.
const isHandOffRoute = (pathname: string | null) =>
  !pathname ||
  pathname === '/' ||
  pathname === '/deep-link' ||
  pathname.startsWith('/profile');

/**
 * Preloads the player once a profile is selected and the user has landed on a
 * real screen (home or a deep link's target), after that screen has had a
 * chance to render.
 *
 * Every way into a session ends with a selected profile : the picker, the PIN
 * screen, and the single-profile auto-select (including deep links that skip
 * the picker) , so it covers all of them.
 */
export function usePlayerPreload(hasSelectedProfile: boolean, pathname: string | null) {
  const isLanded = hasSelectedProfile && !isHandOffRoute(pathname);

  useEffect(() => {
    const { preloadPlayer } = getSystem();
    if (!isLanded || !preloadPlayer) return;

    const w = window as IdleWindow;
    const run = () => void preloadPlayer();

    if (w.requestIdleCallback) {
      const idleId = w.requestIdleCallback(run, {
        timeout: PLAYER_PRELOAD_IDLE_TIMEOUT_MS,
      });
      return () => w.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(run, PLAYER_PRELOAD_FALLBACK_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isLanded]);
}
